/**
 * The domains we know how to spell-check against. If someone types something
 * that looks almost like one of these, we offer the real version as a fix.
 * Vectorsolv is in here too so internal emails don't trigger a suggestion.
 */
const COMMON_DOMAINS = [
  // Internal
  'vectorsolv.com',
  // Google
  'gmail.com',
  'googlemail.com',
  // Yahoo
  'yahoo.com',
  'yahoo.ca',
  'yahoo.co.uk',
  'ymail.com',
  // Microsoft
  'hotmail.com',
  'outlook.com',
  'live.com',
  'live.ca',
  // Apple
  'icloud.com',
];

/**
 * How many character changes to turn `a` into `b`. Each insert, delete, or
 * substitute counts as one. `gmai` → `gmail` is 1. `gmial` → `gmail` is 2.
 *
 * Standard textbook dynamic programming — the loops have to fill the table
 * in order because each cell depends on the ones above/left of it. The first
 * row and column hold the "distance from empty string" base cases.
 */
function levenshtein(a: string, b: string): number {
  // dp[i][j] holds the answer for the first i chars of a and first j chars of b.
  const dp: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1] // same letter — no edit, copy from the diagonal
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]); // delete, insert, or substitute
    }
  }

  return dp[a.length][b.length];
}

/**
 * Look at the email and, if the domain looks like a typo of one we know,
 * hand back the corrected version. Otherwise null — caller can ignore.
 *
 * We don't fix anything that's already a real provider, and we stay quiet
 * on inputs that don't even look like emails. The "close enough to count"
 * threshold is two character changes — catches the common fumbles like
 * `gmai.com` or `yhaoo.com` without false-flagging unrelated domains.
 *
 *   'shpend@gmai.com'      → 'shpend@gmail.com'
 *   'shpend@yhaoo.com'     → 'shpend@yahoo.com'
 *   'shpend@gmail.com'     → null  (already correct)
 *   'shpend@bizjet.aero'   → null  (nothing close)
 *   'not-an-email'         → null
 */
export function suggestEmailDomain(email: string | null | undefined): string | null {
  if (!email || typeof email !== 'string') return null;

  // Trim the whole thing first — common when pasting — so a leading/trailing
  // space doesn't bleed into the local part of the suggestion.
  const trimmed = email.trim();
  const atIdx = trimmed.lastIndexOf('@');
  if (atIdx <= 0 || atIdx === trimmed.length - 1) return null;

  const local = trimmed.slice(0, atIdx);
  const domain = trimmed.slice(atIdx + 1).toLowerCase();

  if (!domain || !local) return null;
  // Real domain we recognise — leave it alone.
  if (COMMON_DOMAINS.includes(domain)) return null;

  // Compare against every known domain, drop the ones too far off, and keep
  // whichever ended up closest. Only ~20 candidates, so don't over-think perf —
  // the pipeline reads cleanly and that's what matters.
  const closest = COMMON_DOMAINS.map(candidate => ({ candidate, distance: levenshtein(domain, candidate) }))
    .filter(({ distance }) => distance > 0 && distance <= 2)
    .reduce<{ candidate: string; distance: number } | null>(
      (best, current) => (best === null || current.distance < best.distance ? current : best),
      null
    );

  return closest ? `${local}@${closest.candidate}` : null;
}
